<script setup lang="ts">
import StepperItem from './onlineSurvey/StepperItem.vue'
import FinishForm from './onlineSurvey/FinishTypes/FinishForm.vue'
import FinishRedirect from './onlineSurvey/FinishTypes/FinishRedirect.vue'
import FinishMessage from './onlineSurvey/FinishTypes/FinishMessage.vue'
import ProgressBar from './onlineSurvey/questionTypes/ProgressBar.vue'
import { useSurvey } from '@/composables/onlineSurvey/useSurvey'
import type { BaseWebComponentProps } from '../types/webComponents'
import { ref, computed, onMounted } from 'vue'
import api from '@/composables/onlineSurvey/api'

const n8nResult = ref<any>(null)
const submittingData = ref(false)
const error = ref(false)
const loadingError = ref(false)
const isLoading = ref(true)
const baseUrl = ref('')
const defaultSurveyId = '40ceaffd-5de0-486a-a270-630ecf6b7735' // Fallback Survey ID

interface Props extends BaseWebComponentProps {
  theme?: 'light' | 'dark'
  primaryColor?: string
  secondaryColor?: string
  surveyId?: string
  environment?: 'production' | 'development' | 'test'
}

// Props mit Standardwerten
const props = withDefaults(defineProps<Props>(), {
  theme: 'light',
  primaryColor: '#108cc7',
  secondaryColor: '#cf4a38',
  surveyId: '',
  environment: 'production',
})

const {
  loadSurvey,
  surveyConfig,
  currentStep,
  answers,
  visibleQuestionIds,
  cleanupInvisibleAnswers,
} = useSurvey()
const finished = ref(false)

onMounted(() => {
  initSurvey()
})

async function initSurvey() {
  try {
    isLoading.value = true

    // URL basierend auf Umgebung wählen
    const baseUrls = {
      production: import.meta.env.VITE_API_BASE_URL_PROD,
      development: import.meta.env.VITE_API_BASE_URL_DEV,
      test: import.meta.env.VITE_API_BASE_URL_TEST,
    }

    baseUrl.value = baseUrls[props.environment]
    let surveyUrl = `${baseUrl.value}surveys/${props.surveyId || defaultSurveyId}`

    console.log('Fetching survey from URL:', surveyUrl, 'Environment:', props.environment)

    let response

    response = await api.get(surveyUrl)

    console.log('Fetching survey from URL:', response)
    const surveyResponse = response
    console.log('Survey response received:', surveyResponse)

    // Prüfen ob die Daten im erwarteten Format vorliegen
    let surveyArray
    let surveyMeta
    if (surveyResponse.data) {
      // Neue Struktur: response.data direkt
      surveyArray = surveyResponse.data.surveyArray
    }
    // Sicherstellen, dass surveyData ein Objekt und kein String ist
    if (typeof surveyArray === 'string') {
      surveyArray = JSON.parse(surveyArray)
    }

    surveyMeta = {
      n8nEndpoint: surveyResponse.data.n8nEndpoint ?? null,
      finish: {
        type: surveyResponse.data.finishType ?? null,
      },
      finishConfig: surveyResponse.data.finishConfig ?? null,
      benefitText: surveyResponse.data.benefitText ?? null,
      aiPrompt: surveyResponse.data.aiPrompt ?? null,
    }

    loadSurvey(surveyArray, surveyMeta)
    loadingError.value = false
  } catch (e) {
    console.error('Could not load survey config from remote URL:', e)
    loadingError.value = true
  } finally {
    isLoading.value = false
    cleanupInvisibleAnswers()
  }
}

async function handleSurveyCompletion() {
  console.log('Survey completed, preparing to post data...', surveyConfig.value.finish.type)
  if (surveyConfig.value && surveyConfig.value.finish.type !== 'formular') {
    await postData()
  }
  finished.value = true
}

async function postData() {
  console.log('Posting survey data to endpoint...')

  // Endpoint basierend auf Umgebung wählen
  const defaultEndpoints = {
    production: 'https://n8n.simki.cloud/webhook/278d8901-f8d9-49b5-9cc5-aa3a6e94bbd7',
    development: 'https://n8n.simki.cloud/webhook/278d8901-f8d9-49b5-9cc5-aa3a6e94bbd7',
    test: 'https://n8n.simki.cloud/webhook/278d8901-f8d9-49b5-9cc5-aa3a6e94bbd7',
  }

  const payload = {
    answers: answers.value,
    surveyId: props.surveyId,
    aiPrompt: surveyConfig.value?.aiPrompt || null,
    benefitText: surveyConfig.value?.benefitText || null,
  }

  try {
    submittingData.value = true
    const endPoint = surveyConfig.value?.n8nEndpoint ?? defaultEndpoints[props.environment]

    console.log('Posting to endpoint:', endPoint, 'Environment:', props.environment)
    const result = await fetch(endPoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!result.ok) throw new Error(`HTTP error! status: ${result.status}`)

    // Versuche JSON zu parsen, falls Response Content-Type JSON ist
    let responseData
    const contentType = result.headers.get('content-type')

    if (contentType && contentType.includes('application/json')) {
      responseData = await result.json()
      console.log('Server response (JSON):', responseData)
      n8nResult.value = responseData
    } else {
      // Fallback für Text/HTML Response
      responseData = await result.text()
      console.log('Server response (Text):', responseData)
      n8nResult.value = responseData
    }
  } catch (e) {
    console.error('Error posting survey data:', e)
    // Fallback-Response für Fehlerbehandlung
    n8nResult.value = {
      message: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
    }
    error.value = true
  } finally {
    submittingData.value = false
  }
}

const answeredQuestions = ref(0)

function updateAnsweredCount() {
  answeredQuestions.value = answers.value.length
}

const activeStep = computed(() => {
  if (!surveyConfig.value || !surveyConfig.value.steps) return null
  return surveyConfig.value.steps[currentStep.value] || null
})
</script>
<template>
  <!-- Loading State -->
  <div v-if="isLoading" class="online-survey" :class="`theme-${theme}`">
    <div class="p-6 has-text-centered">
      <div class="button is-primary is-loading is-large">Survey wird geladen...</div>
    </div>
  </div>

  <!-- Error State -->
  <div v-else-if="loadingError" class="online-survey" :class="`theme-${theme}`">
    <div class="p-4">
      <div class="notification is-danger">
        <strong>Fehler beim Laden der Survey</strong>
        <p>
          Die Survey konnte nicht geladen werden. Bitte versuchen Sie es später erneut oder
          kontaktieren Sie den Support.
        </p>
      </div>
    </div>
  </div>

  <!-- Survey Content - nur wenn erfolgreich geladen -->
  <div
    v-else-if="surveyConfig && !loadingError"
    class="online-survey"
    :class="`theme-${theme}`"
    :style="{ '--primary-color': primaryColor, '--secondary-color': secondaryColor }"
  >
    <header
      class="is-flex is-flex-direction-column is-justify-content-center is-align-items-center px-4 py-4 has-text-centered"
    >
      <div v-if="currentStep == 0">
        <h1 class="is-size-4-desktop is-size-5-tablet has-text-weight-semibold">
          {{ surveyConfig.title }}
        </h1>
        <p class="py-4">{{ surveyConfig.description }}</p>
      </div>
      <ProgressBar
        v-if="!finished"
        :answeredQuestions="answeredQuestions"
        :totalQuestions="visibleQuestionIds.size"
      />
    </header>

    <Transition name="fade" mode="out-in">
      <div
        v-if="!submittingData && activeStep && !finished"
        :key="activeStep.id"
        class="survey-step"
      >
        <StepperItem
          :step="activeStep"
          @surveryCompleted="handleSurveyCompletion"
          @moveSteps="updateAnsweredCount"
          class="p-4"
        />
      </div>
    </Transition>

    <!-- Post-Submit Error -->
    <div v-if="error && finished" class="p-4">
      <div class="notification">
        <strong>Fehler beim Senden der Daten:</strong>
        <p>
          {{
            n8nResult?.message ||
            'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.'
          }}
        </p>
      </div>
    </div>
    <!-- Submitting State -->
    <div v-if="submittingData" class="p-6 has-text-centered">
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <p class="mt-4 is-size-5 has-text-weight-medium">Eingaben werden analysiert...</p>
        <p class="is-size-7 has-text-grey">Bitte warten Sie einen Moment</p>
      </div>
    </div>
    <!-- Finish States -->
    <div v-else-if="!submittingData && finished && !error" class="p-4">
      <FinishRedirect
        v-if="surveyConfig.finish.type === 'redirect'"
        :redirectUrl="surveyConfig.finishConfig ?? ''"
      />
      <FinishMessage
        v-if="surveyConfig.finish.type === 'message'"
        :message="n8nResult?.output ?? 'Vielen Dank für Ihre Teilnahme!'"
      />
      <FinishForm
        v-if="surveyConfig.finish.type === 'formular'"
        @formCompleted="postData"
        :uuid="surveyConfig.finishConfig ?? ''"
        :theme="theme"
        :primaryColor="primaryColor"
        :message="n8nResult?.output"
        :baseUrl="baseUrl"
      />
    </div>
  </div>
</template>

<style>
/* Primary Color Override mit CSS Custom Properties */
.button.is-primary {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  color: white !important;
}

.button.is-loading {
  color: transparent !important;
}

.button.is-primary:hover:not(:disabled) {
  background-color: color-mix(in srgb, var(--primary-color) 85%, black) !important;
  border-color: color-mix(in srgb, var(--primary-color) 85%, black) !important;
}

.button.is-primary:focus {
  box-shadow: 0 0 0 0.125em color-mix(in srgb, var(--primary-color) 25%, transparent) !important;
}

/* Weißer Spinner für Primary Button */
.button.is-primary.is-loading::after {
  border-color: white !important;
  border-right-color: transparent !important;
  border-top-color: transparent !important;
}

/* Custom Loading Spinner */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--bulma-border, #e0e0e0);
  border-radius: 50%;
  border-top-color: var(--primary-color, #3273dc);
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Loading Spinner */
@media (max-width: 768px) {
  .loading-spinner {
    width: 40px;
    height: 40px;
    border-width: 3px;
  }

  .loading-container {
    min-height: 150px;
    border-top-color: transparent !important;
  }
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.online-survey .input,
.online-survey button {
  align-items: center;
  appearance: none;
  border-radius: 0.375rem;
  border-style: solid;
  border-width: 1px;
  box-shadow: none;
  display: inline-flex;
  font-size: 1rem;
  height: 2.25rem;
  justify-content: flex-start;
  line-height: 1.5;
  padding: calc(0.75em - 1px);
  position: relative;
  transition-duration: 86ms;
  transition-property: background-color, border-color, box-shadow, color;
  vertical-align: top;
  border: 2px solid var(--bulma-border, #dbdbdb);
}
</style>
